<template>
  <!-- 
    PokemonCard Component
    =====================
    Single Responsibility: Display one Pokemon card with loading state
    
    This component:
    - Handles its own image loading
    - Shows loading skeleton while image loads
    - Emits events for parent orchestration
    - Uses intersection observer for lazy loading
    - Applies staggered animation based on delay prop
  -->
  <div 
    class="pokemon-card-wrapper"
    :class="{ 
      'is-loaded': isLoaded,
      'is-loading': !isLoaded 
    }"
    :style="cardStyles"
    ref="cardElement"
  >
    <!-- Loading skeleton -->
    <div v-if="!isLoaded" class="card-skeleton">
      <div class="skeleton-shimmer"></div>
    </div>
    
    <!-- Actual card image -->
    <img
      v-if="shouldLoad"
      :src="finalImageSrc"
      :alt="imageAlt"
      class="pokemon-card-image"
      @load="handleImageLoad"
      @error="handleImageError"
      loading="lazy"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue'

export default {
  name: 'PokemonCard',
  
  props: {
    // Image source URL for the card
    imageSrc: {
      type: String,
      required: true
    },
    
    // Alt text for accessibility
    imageAlt: {
      type: String,
      default: 'Pokemon Card'
    },
    
    // Unique identifier for this card
    cardId: {
      type: [String, Number],
      required: true
    },
    
    // Animation delay in milliseconds for staggered effect
    delay: {
      type: Number,
      default: 0
    },
    
    // Whether to immediately load or wait for intersection
    eagerLoad: {
      type: Boolean,
      default: false
    }
  },
  
  emits: ['loaded', 'error'],
  
  setup(props, { emit }) {
    // Component state
    const isLoaded = ref(false)
    const shouldLoad = ref(props.eagerLoad)
    const cardElement = ref(null)
    const imageError = ref(false)
    let intersectionObserver = null

    /**
     * Computed styles for the card wrapper
     * Applies animation delay for staggered entrance effect
     */
    const cardStyles = computed(() => ({
      animationDelay: `${props.delay}ms`
    }))

    /**
     * Computed image source with fallback
     * Returns placeholder if original image failed to load
     */
    const finalImageSrc = computed(() => {
      if (imageError.value) {
        return '/images/placeholders/card-thumbnail.svg'
      }
      return props.imageSrc || '/images/placeholders/card-thumbnail.svg'
    })

    /**
     * Handle successful image load
     * Emits loaded event for parent tracking
     */
    const handleImageLoad = () => {
      isLoaded.value = true
      emit('loaded', props.cardId)
    }

    /**
     * Handle image loading error
     * Fallback to placeholder image
     */
    const handleImageError = (error) => {
      console.warn(`Failed to load card image: ${props.imageSrc}`, error)
      imageError.value = true
      emit('error', { cardId: props.cardId, error })
      // Still mark as loaded to prevent infinite loading state
      isLoaded.value = true
    }
    
    /**
     * Setup intersection observer for lazy loading
     * Only loads image when card is near viewport
     */
    const setupIntersectionObserver = () => {
      if (!cardElement.value || props.eagerLoad) return
      
      const options = {
        // Start loading when card is 200px away from viewport
        rootMargin: '200px 0px',
        threshold: 0.01
      }
      
      intersectionObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !shouldLoad.value) {
            shouldLoad.value = true
            // Once loaded, no need to observe anymore
            intersectionObserver.disconnect()
          }
        })
      }, options)
      
      intersectionObserver.observe(cardElement.value)
    }
    
    // Lifecycle hooks
    onMounted(() => {
      setupIntersectionObserver()
    })
    
    onUnmounted(() => {
      if (intersectionObserver) {
        intersectionObserver.disconnect()
      }
    })
    
    return {
      isLoaded,
      shouldLoad,
      cardElement,
      cardStyles,
      finalImageSrc,
      handleImageLoad,
      handleImageError
    }
  }
}
</script>

<style scoped>
/**
 * Pokemon Card Styles
 * Uses CSS custom properties for easy theming
 * Integrates with site's 8px baseline grid
 */

/* CSS Custom Properties for card dimensions */
.pokemon-card-wrapper {
  --card-width: 70px;
  --card-height: 98px;
  --card-radius: 6px;
  --card-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  
  position: relative;
  width: var(--card-width);
  height: var(--card-height);
  border-radius: var(--card-radius);
  overflow: hidden;
  flex-shrink: 0; /* Prevent cards from shrinking in flex container */
  
  /* GPU acceleration for smooth animations */
  transform: translateZ(0);
  will-change: transform, opacity;
  
  /* Simple fade-in animation */
  animation: cardFadeIn 0.4s ease-out forwards;
  opacity: 0;
}

/* Responsive card sizing */
@media (min-width: 768px) {
  .pokemon-card-wrapper {
    --card-width: 80px;
    --card-height: 112px;
  }
}

@media (min-width: 1024px) {
  .pokemon-card-wrapper {
    --card-width: 90px;
    --card-height: 126px;
  }
}

/* Loading skeleton */
.card-skeleton {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.1) 0%,
    rgba(255, 255, 255, 0.2) 50%,
    rgba(255, 255, 255, 0.1) 100%
  );
  border-radius: var(--card-radius);
}

/* Shimmer animation for loading state */
.skeleton-shimmer {
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.3) 50%,
    transparent 100%
  );
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  to {
    left: 100%;
  }
}

/* Card image */
.pokemon-card-image {
  width: 100%;
  height: 100%;
  object-fit: contain; /* Show full card */
  border-radius: var(--card-radius);
  box-shadow: var(--card-shadow);
  background: rgba(255, 255, 255, 0.05); /* Light background */
  
  /* Make cards fully visible - no dimming */
  filter: brightness(1) contrast(1.05);
  opacity: 1 !important;
  
  /* Prevent blurry images */
  image-rendering: auto;
  
  /* Smooth transition when loaded */
  transition: opacity 0.3s ease-out;
}

/* Loaded state */
.is-loaded .pokemon-card-image {
  opacity: 1;
}

.is-loading .pokemon-card-image {
  opacity: 0;
}

/* Simple fade-in animation */
@keyframes cardFadeIn {
  0% {
    opacity: 0;
    transform: scale(0.9);
  }
  100% {
    opacity: 1; /* Full opacity - no transparency */
    transform: scale(1);
  }
}

/* Performance optimization */
.pokemon-card-wrapper,
.pokemon-card-image {
  backface-visibility: hidden;
  -webkit-font-smoothing: antialiased;
}
</style>